/**
 * Per-hash referral data: document structure and paths.
 * Shared by client refs and admin (e.g. addreferralcodeowner).
 */
import {z} from 'zod';

import type {CourseId} from '../../../types';

// ============================================================================
// Schema (Zod)
// ============================================================================

/**
 * Zod schema for referral data as stored in Firestore.
 * Uses optional/default for backwards compatibility with legacy documents.
 * String fields are Firebase Auth UIDs (UserId from @cip/core/types).
 *
 * cip5, pylatino-25 used different hash functions than new courses.
 * cip4 used a different structure; see application client for history link.
 */
const PerHashReferralDataSchemaBase = z
  .object({
    /** The user id of the original user who created the referral code. May be undefined for external codes. */
    referrerId: z.string().optional(),
    /** Additional users who have ownership access. If empty/undefined, falls back to referrerId. */
    owners: z.array(z.string()).optional(),
    /** User ids of students referred by this code. */
    referredStudents: z.array(z.string()).default([]),
    /** User ids of teachers referred by this code. */
    referredTeachers: z.array(z.string()).default([]),
    /** Whether this code is hidden from the public leaderboard. */
    hideInLeaderboard: z.boolean().default(false),
    /** Denormalized count of unique users referred. Used for leaderboard sorting/pagination. */
    totalReferrals: z.number().optional(),
  })
  .loose();

type PerHashReferralDataInput = z.infer<typeof PerHashReferralDataSchemaBase>;

export const PerHashReferralDataSchema =
  PerHashReferralDataSchemaBase.transform((data: PerHashReferralDataInput) => {
    const referredStudents = data.referredStudents;
    const referredTeachers = data.referredTeachers;
    const totalReferrals =
      data.totalReferrals ??
      new Set([...referredStudents, ...referredTeachers]).size;
    return {
      ...data,
      referredStudents,
      referredTeachers,
      totalReferrals,
    };
  });

export type PerHashReferralData = z.infer<typeof PerHashReferralDataSchema>;

/**
 * Default/empty referral data with all schema defaults applied.
 * Use when you need a fallback (e.g. merging with partial state).
 */
export const defaultPerHashReferralData: PerHashReferralData =
  PerHashReferralDataSchema.parse({});

/**
 * Type guard for PerHashReferralData using the Zod schema.
 */
export const isPerHashReferralData = (
  data: unknown,
): data is PerHashReferralData =>
  PerHashReferralDataSchema.safeParse(data).success;

// ============================================================================
// Paths
// ============================================================================

export const getPerHashReferralDataCollectionPath = (courseId: CourseId) => {
  return `applicationReferrals/${courseId}/hashes`;
};

/**
 * Firestore document path for per-hash referral data.
 * Shared by client ({@link getPerHashReferralDataDocRef}) and admin (e.g. addreferralcodeowner).
 */
export function getPerHashReferralDataDocPath(
  courseId: CourseId,
  hash: string,
): string {
  return `${getPerHashReferralDataCollectionPath(courseId)}/${hash}`;
}
